<template>
    <div dir="rtl" class="min-h-full">
        <!-- Mobile menu button -->
        <div class="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-gray-200">
            <div class="flex items-center justify-between px-4 py-3">
                <img class="h-8 w-auto" src="@/assets/logo2.jpg" alt="logo" />
                <button
                    @click="toggleMobileMenu"
                    type="button"
                    class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                    aria-controls="mobile-menu"
                    :aria-expanded="mobileMenuOpen">
                    <span class="sr-only">فتح القائمة الرئيسية</span>
                    <!-- Hamburger icon -->
                    <svg v-if="!mobileMenuOpen" class="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                    <!-- Close icon -->
                    <svg v-else class="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Desktop toggle button (when sidebar is hidden) -->
        <div v-if="!desktopSidebarOpen" class="hidden lg:block fixed top-4 right-4 z-50">
            <button
                @click="toggleDesktopSidebar"
                type="button"
                class="inline-flex items-center justify-center p-3 rounded-md bg-white shadow-lg border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                title="فتح القائمة الجانبية">
                <span class="sr-only">فتح القائمة الجانبية</span>
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>
        </div>

        <!-- Mobile menu overlay -->
        <div v-if="mobileMenuOpen" class="lg:hidden fixed inset-0 z-40">
            <!-- Background overlay -->
            <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeMobileMenu"></div>
            
            <!-- Mobile menu panel -->
            <div class="fixed inset-y-0 right-0 max-w-xs w-full bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
                <div class="flex flex-col h-full">
                    <!-- Mobile menu header -->
                    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <img class="h-8 w-auto" src="@/assets/logo2.jpg" alt="logo" />
                        <button
                            @click="closeMobileMenu"
                            type="button"
                            class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100">
                            <span class="sr-only">إغلاق القائمة</span>
                            <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- Mobile navigation -->
                    <nav class="flex-1 px-4 py-4 overflow-y-auto">
                        <div class="space-y-1">
                            <RouterLink v-if="canAccessRoute('/hafed')" to="/hafed"
                                @click="closeMobileMenu"
                                class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                <span class="ml-4 flex-shrink-0 h-6 w-6 flex items-center justify-center text-base">✦</span>
                                حافظ
                            </RouterLink>

                            <RouterLink v-if="!isSuperAdmin && canAccessRoute('/dashboard')" to="/dashboard"
                                @click="closeMobileMenu"
                                class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                <component :is="ViewGridIcon"
                                    class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                    aria-hidden="true" />
                                لوحة المعلومات
                            </RouterLink>

                            <RouterLink v-if="canAccessRoute('/support-tickets')" to="/support-tickets"
                                @click="closeMobileMenu"
                                class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                <component :is="TemplateIcon"
                                    class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                    aria-hidden="true" />
                                البلاغات
                            </RouterLink>

                            <!-- Mobile Reports Section -->
                            <div v-if="!isSuperAdmin && canAccessRoute('/sendMes')" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>التقارير</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>

                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">

                                        <RouterLink v-if="canAccessRoute('/sendMes')" to="/sendMes"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            ارسال التقارير
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/session-report')" to="/session-report"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تقرير المواعيد
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/SummarySessionReport')" to="/SummarySessionReport"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تقرير الجلسات
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/DailyStudentReport')" to="/DailyStudentReport"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تقرير الدارسين
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/iqra/teacher-response-report')" to="/iqra/teacher-response-report"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تقرير ردود الجلسات
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/peer-review-report')" to="/peer-review-report"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تقرير جلسات التعاهد
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Teachers Section -->
                            <div v-if="!isSuperAdmin && canAccessRoute('/view/teachers')" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TableIcon"
                                            class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>المعلمين</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>
                                    <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                        <RouterLink v-if="canAccessRoute('/add/teacher')" to="/add/teacher"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            انشاء معلم
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/view/teachers')" to="/view/teachers"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            المعلمين المسجلين
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Students Section -->
                            <div v-if="!isSuperAdmin && (canAccessRoute('/students') || canAccessRoute('/new/student') || canAccessRoute('/add/student'))" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>الدارسين</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>

                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">
                                        <RouterLink v-if="canAccessRoute('/add/student')" to="/add/student"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إضافة دارس
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/students')" to="/students"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            الدارسين المفعلين
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/new/student')" to="/new/student"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            الدارسين الغير مفعلين
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/allStudents')" to="/allStudents"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            الدارسين المسجلين
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/student-calendar')" to="/student-calendar"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تقويم الدارس
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Sessions Section -->
                            <div v-if="!isSuperAdmin && canAccessRoute('/sessions')" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>إدارة الجلسات</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>

                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">
                                        <RouterLink v-if="canAccessRoute('/sessions')" to="/sessions"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تعديل الجلسات
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/student-sessions-management')" to="/student-sessions-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة جلسات الدارسين
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/admin-student-booking')" to="/admin-student-booking"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            حجز وإلغاء المواعيد
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Public Circles Section -->
                            <div v-if="!isSuperAdmin && canAccessRoute('/public-circles-management')" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon"
                                            class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>إدارة الحلقات العامة</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>
                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">
                                        <RouterLink to="/public-circles-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة الحلقات
                                        </RouterLink>
                                        <RouterLink to="/public-circles-management/attendance-logs"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            سجل دخول الطلاب
                                        </RouterLink>
                                        <RouterLink to="/public-circles-management/daily-chat-logs"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            سجل المحادثات اليومية
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Iqra Section -->
                            <div v-if="!isSuperAdmin && canAccessRoute('/iqra/schedules')" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon"
                                            class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>إدارة الجلسات الجديدة</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>
                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">
                                        <RouterLink v-if="canAccessRoute('/studentSessions')" to="/studentSessions"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            الجلسات
                                        </RouterLink>
                                        <RouterLink to="/iqra/schedules"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            جداول الجلسات الجديدة
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/live-sessions')" to="/live-sessions"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            الجلسات الحالية
                                        </RouterLink>
                                        <RouterLink to="/iqra/teacher-weekly-schedule"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            جدول المعلمين الأسبوعي
                                        </RouterLink>
                                        <RouterLink to="/iqra/student-weekly-schedule"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            جدول الطلاب الأسبوعي
                                        </RouterLink>
                                        <RouterLink to="/iqra/student-slot-enrollments"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            ربط الطالب بالفترة
                                        </RouterLink>
                                        <RouterLink to="/iqra/prayer-times-sync-status"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            حالة مزامنة أوقات الصلاة
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Subscriptions Section -->
                            <div v-if="!isSuperAdmin && (canAccessRoute('/iqra/subscription-packages') || canAccessRoute('/iqra/student-subscriptions'))" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon"
                                            class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>الاشتراكات</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>
                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">
                                        <RouterLink v-if="canAccessRoute('/iqra/subscription-packages')" to="/iqra/subscription-packages"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            باقات الاشتراك
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/iqra/student-subscriptions')" to="/iqra/student-subscriptions"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            اشتراكات الطلاب
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Admins Section -->
                            <div v-if="!isSuperAdmin && (canAccessRoute('/addAdmin') || canAccessRoute('/allAdmins'))" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>الإداريين</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>

                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">
                                        <RouterLink v-if="canAccessRoute('/addAdmin')" to="/addAdmin"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            اضافة اداري
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/allAdmins')" to="/allAdmins"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            جميع الإداريين
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Tenants Section (Super Admin only) -->
                            <div v-if="isSuperAdmin" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>الفروع</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>

                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">
                                        <RouterLink to="/tenants-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة الفروع
                                        </RouterLink>
                                        <RouterLink to="/general-managers-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة مدراء الفروع
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile App Settings Section -->
                            <div v-if="!isSuperAdmin" class="w-full rounded-md bg-white">
                                <Disclosure v-slot="{ open }">
                                    <DisclosureButton
                                        class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                        <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                            aria-hidden="true" />
                                        <span>إدارة التطبيق</span>
                                        <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                            class="h-5 w-5 text-primary mr-auto" />
                                    </DisclosureButton>

                                    <DisclosurePanel class="pt-2 pb-2 space-y-1 text-sm text-gray-500 pr-8">
                                        <RouterLink v-if="canAccessRoute('/appSetting')" to="/appSetting"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تعديل اعدادات التطبيق
                                        </RouterLink>
                                        <RouterLink v-if="isAdmin && canAccessRoute('/about-us-management')" to="/about-us-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة عن المقرأة
                                        </RouterLink>
                                        <RouterLink v-if="isAdmin && canAccessRoute('/contact-us-management')" to="/contact-us-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة تواصل معنا
                                        </RouterLink>
                                        <RouterLink v-if="isAdmin && canAccessRoute('/terms-and-conditions-management')" to="/terms-and-conditions-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة الشروط والأحكام
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/organizations')" to="/organizations"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة الجهات
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/qualifications-management')" to="/qualifications-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة المؤهلات
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/durations-management')" to="/durations-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة المدد
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/massarats-management')" to="/massarats-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة المسارات
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/regions-management')" to="/regions-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة المناطق
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/athan-times-management')" to="/athan-times-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة أوقات الأذان
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/goals-management')" to="/goals-management"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إدارة الأهداف
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/admin-messages')" to="/admin-messages"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            رسائل حفظة
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/admin-notifications')" to="/admin-notifications"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            إشعارات الإدارة
                                        </RouterLink>
                                        <RouterLink v-if="canAccessRoute('/audit-logs')" to="/audit-logs"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            سجل التغييرات
                                        </RouterLink>
                                        <RouterLink v-if="isAdmin || isGeneralManager" to="/permission-groups"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            مجموعات الصلاحيات
                                        </RouterLink>
                                        <RouterLink v-if="isAdmin || isGeneralManager" to="/user-permissions"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            صلاحيات المستخدمين
                                        </RouterLink>
                                        <RouterLink v-if="isAdmin || isGeneralManager" to="/ai-tool-permissions"
                                            @click="closeMobileMenu"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            صلاحيات المساعد الذكي
                                        </RouterLink>
                                    </DisclosurePanel>
                                </Disclosure>
                            </div>

                            <!-- Mobile Logout -->
                            <div class="w-full pt-4 border-t border-gray-200 mt-4">
                                <p class="text-center text-red-500 py-2 cursor-pointer hover:bg-red-50 rounded-md" @click="logout()">تسجيل الخروج</p>
                            </div>
                        </div>
                    </nav>
                </div>
            </div>
        </div>

        <!-- Desktop sidebar with toggle functionality -->
        <div v-if="desktopSidebarOpen" class="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:shadow-lg transition-all duration-300 ease-in-out">
            <!-- Sidebar component, swap this element with another sidebar if you like -->
            <div class="flex flex-col flex-grow bg-white pt-5 pb-4 overflow-y-auto">
                <div class="flex flex-col items-center px-4 mb-2 space-y-3">
                    <img class="h-13 w-auto fill-customHafadhColor" src="@/assets/logo2.jpg" alt="logo" />
                    <!-- Desktop toggle button (inside sidebar) - positioned under the logo -->
                    <button
                        @click="toggleDesktopSidebar"
                        type="button"
                        class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                        title="إخفاء القائمة الجانبية">
                        <span class="sr-only">إخفاء القائمة الجانبية</span>
                        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                </div>
                <nav class="mt-5 flex-1 flex flex-col divide-y divide-customHafadhColor overflow-y-auto"
                    aria-label="Sidebar">
                    <!-- active class bg-blue-800 text-white -->
                    <div class="px-2 space-y-1">
                        <RouterLink v-if="isSuperAdmin" to="/super-admin-general-report"
                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                            <component :is="ViewGridIcon"
                                class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500 group-[.active]:text-white"
                                aria-hidden="true" />
                            التقرير العام
                        </RouterLink>
                        <RouterLink v-if="canAccessRoute('/hafed')" to="/hafed"
                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                            <span class="ml-4 flex-shrink-0 h-6 w-6 flex items-center justify-center text-base">✦</span>
                            حافظ
                        </RouterLink>

                        <RouterLink v-if="!isSuperAdmin && canAccessRoute('/dashboard')" to="/dashboard"
                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                            <component :is="ViewGridIcon"
                                class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500 group-[.active]:text-white"
                                aria-hidden="true" />
                            لوحة المعلومات
                        </RouterLink>

                        <RouterLink v-if="canAccessRoute('/support-tickets')" to="/support-tickets"
                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                            <component :is="TemplateIcon"
                                class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500 group-[.active]:text-white"
                                aria-hidden="true" />
                            البلاغات
                        </RouterLink>
                        <div v-if="!isSuperAdmin && canAccessRoute('/sendMes')" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                        aria-hidden="true" />
                                    <span>التقارير</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>

                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">

                                   <RouterLink v-if="canAccessRoute('/sendMes')" to="/sendMes"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        ارسال التقارير
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/session-report')" to="/session-report"
                                            class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                            تقرير المواعيد
                                        </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/SummarySessionReport')" to="/SummarySessionReport"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        تقرير الجلسات
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/DailyStudentReport')" to="/DailyStudentReport"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        تقرير الدارسين
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/iqra/teacher-response-report')" to="/iqra/teacher-response-report"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        تقرير ردود الجلسات
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/peer-review-report')" to="/peer-review-report"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        تقرير جلسات التعاهد
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>
                        <div v-if="!isSuperAdmin && canAccessRoute('/view/teachers')" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TableIcon"
                                        class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500 group-[.active]:text-white"
                                        aria-hidden="true" />
                                    <span>المعلمين</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>
                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink v-if="canAccessRoute('/add/teacher')" to="/add/teacher"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        انشاء معلم
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/view/teachers')" to="/view/teachers"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        المعلمين المسجلين
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>
                        <div v-if="!isSuperAdmin && (canAccessRoute('/students') || canAccessRoute('/new/student') || canAccessRoute('/add/student'))" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                        aria-hidden="true" />
                                    <span>الدارسين</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>

                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink v-if="canAccessRoute('/add/student')" to="/add/student"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إضافة دارس
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/students')" to="/students"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        الدارسين المفعلين
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/new/student')" to="/new/student"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        الدارسين الغير مفعلين
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/allStudents')" to="/allStudents"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        الدارسين المسجلين
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/student-calendar')" to="/student-calendar"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        تقويم الدارس
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>
                        <div v-if="!isSuperAdmin && canAccessRoute('/sessions')" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                        aria-hidden="true" />
                                    <span>إدارة الجلسات</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>

                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink v-if="canAccessRoute('/sessions')" to="/sessions"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        تعديل الجلسات
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/student-sessions-management')" to="/student-sessions-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة جلسات الدارسين
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/admin-student-booking')" to="/admin-student-booking"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        حجز وإلغاء المواعيد
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>

                        <div v-if="!isSuperAdmin && canAccessRoute('/public-circles-management')" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon"
                                        class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500 group-[.active]:text-white"
                                        aria-hidden="true" />
                                    <span>إدارة الحلقات العامة</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>
                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink to="/public-circles-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة الحلقات
                                    </RouterLink>
                                    <RouterLink to="/public-circles-management/attendance-logs"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        سجل دخول الطلاب
                                    </RouterLink>
                                    <RouterLink to="/public-circles-management/daily-chat-logs"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        سجل المحادثات اليومية
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>

                        <div v-if="!isSuperAdmin && canAccessRoute('/iqra/schedules')" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon"
                                        class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500 group-[.active]:text-white"
                                        aria-hidden="true" />
                                    <span>إدارة الجلسات الجديدة</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>
                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink v-if="canAccessRoute('/studentSessions')" to="/studentSessions"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        الجلسات
                                    </RouterLink>
                                    <RouterLink to="/iqra/schedules"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        جداول الجلسات الجديدة
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/live-sessions')" to="/live-sessions"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        الجلسات الحالية
                                    </RouterLink>
                                    <RouterLink to="/iqra/teacher-weekly-schedule"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        جدول المعلمين الأسبوعي
                                    </RouterLink>
                                    <RouterLink to="/iqra/student-weekly-schedule"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        جدول الطلاب الأسبوعي
                                    </RouterLink>
                                    <RouterLink to="/iqra/student-slot-enrollments"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        ربط الطالب بالفترة
                                    </RouterLink>
                                    <RouterLink to="/iqra/prayer-times-sync-status"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        حالة مزامنة أوقات الصلاة
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>

                        <div v-if="!isSuperAdmin && (canAccessRoute('/iqra/subscription-packages') || canAccessRoute('/iqra/student-subscriptions'))" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon"
                                        class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500 group-[.active]:text-white"
                                        aria-hidden="true" />
                                    <span>الاشتراكات</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>
                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink v-if="canAccessRoute('/iqra/subscription-packages')" to="/iqra/subscription-packages"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        باقات الاشتراك
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/iqra/student-subscriptions')" to="/iqra/student-subscriptions"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        اشتراكات الطلاب
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>

                        <div v-if="!isSuperAdmin && (canAccessRoute('/addAdmin') || canAccessRoute('/allAdmins'))" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                        aria-hidden="true" />
                                    <span>الإداريين</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>

                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink v-if="canAccessRoute('/addAdmin')" to="/addAdmin"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        اضافة اداري
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/allAdmins')" to="/allAdmins"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        جميع الإداريين
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>

                        <!-- Desktop Tenants Section (Super Admin only) -->
                        <div v-if="isSuperAdmin" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                        aria-hidden="true" />
                                    <span>الفروع</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>

                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink to="/tenants-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة الفروع
                                    </RouterLink>
                                    <RouterLink to="/general-managers-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة مدراء الفروع
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>

                        <div v-if="!isSuperAdmin" class="mx-auto w-full max-w-md rounded-md bg-white">
                            <Disclosure v-slot="{ open }">
                                <DisclosureButton
                                    class="w-full justify-start rounded-md text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium">
                                    <component :is="TemplateIcon" class="ml-4 flex-shrink-0 h-6 w-6 text-gray-500"
                                        aria-hidden="true" />
                                    <span>إدارة التطبيق</span>
                                    <ChevronLeftIcon :class="open ? '-rotate-90 transform' : ''"
                                        class="h-5 w-5 text-customHafadhColor mr-auto" />
                                </DisclosureButton>

                                <DisclosurePanel class="pt-4 pb-2 space-y-1 text-sm text-gray-500">
                                    <RouterLink v-if="canAccessRoute('/appSetting')" to="/appSetting"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        تعديل اعدادات التطبيق
                                    </RouterLink>
                                    <RouterLink v-if="isAdmin && canAccessRoute('/about-us-management')" to="/about-us-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة عن المقرأة
                                    </RouterLink>
                                    <RouterLink v-if="isAdmin && canAccessRoute('/contact-us-management')" to="/contact-us-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة تواصل معنا
                                    </RouterLink>
                                    <RouterLink v-if="isAdmin && canAccessRoute('/terms-and-conditions-management')" to="/terms-and-conditions-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة الشروط والأحكام
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/organizations')" to="/organizations"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة الجهات
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/qualifications-management')" to="/qualifications-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة المؤهلات
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/durations-management')" to="/durations-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة المدد
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/massarats-management')" to="/massarats-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة المسارات
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/regions-management')" to="/regions-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة المناطق
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/athan-times-management')" to="/athan-times-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة أوقات الأذان
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/goals-management')" to="/goals-management"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إدارة الأهداف
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/admin-messages')" to="/admin-messages"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        رسائل حفظة
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/admin-notifications')" to="/admin-notifications"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        إشعارات الإدارة
                                    </RouterLink>
                                    <RouterLink v-if="canAccessRoute('/audit-logs')" to="/audit-logs"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        سجل التغييرات
                                    </RouterLink>
                                    <RouterLink v-if="(isAdmin || isGeneralManager) && canAccessRoute('/permission-groups')" to="/permission-groups"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        مجموعات الصلاحيات
                                    </RouterLink>
                                    <RouterLink v-if="(isAdmin || isGeneralManager) && canAccessRoute('/user-permissions')" to="/user-permissions"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        صلاحيات المستخدمين
                                    </RouterLink>
                                    <RouterLink v-if="(isAdmin || isGeneralManager) && canAccessRoute('/ai-tool-permissions')" to="/ai-tool-permissions"
                                        class="text-gray-500 hover:bg-gray-100 group flex items-center px-2 py-2 text-sm leading-6 font-medium rounded-md">
                                        صلاحيات المساعد الذكي
                                    </RouterLink>
                                </DisclosurePanel>
                            </Disclosure>
                        </div>
                        <div mx-auto w-full max-w-md rounded-md bg-white>
                            <p class="text-center text-red-500 pt-5 cursor-pointer" @click="logout()">تسجيل الخروج</p>
                        </div>

                    </div>

                </nav>
            </div>
        </div>
    </div>
</template>

<script>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { TableIcon, TemplateIcon, ViewGridIcon } from '@heroicons/vue/outline';
import { ChevronLeftIcon } from '@heroicons/vue/solid';
import url from '@/url';
import axios from 'axios';
import { usePermissions } from '@/composables/usePermissions';

// Initialize permissions composable
const permissionsComposable = usePermissions();

export default {
    components: {
        Disclosure,
        DisclosureButton,
        DisclosurePanel,
        TableIcon,
        TemplateIcon,
        ViewGridIcon,
        ChevronLeftIcon,
    },
    data() {
        return {
            mobileMenuOpen: false,
            desktopSidebarOpen: true, // Default to open
        }
    },
    mounted() {
        // Load permissions when component is mounted
        // Only load if user is authenticated and not Super Admin
        const userRole = localStorage.getItem('userRole');
        if (userRole && userRole !== 'SUPER_ADMIN') {
            permissionsComposable.fetchUserPermissions();
        }
    },
    watch: {
        desktopSidebarOpen(newValue) {
            // Emit custom event for parent components to listen
            window.dispatchEvent(new CustomEvent('sidebarToggle', { 
                detail: { isOpen: newValue } 
            }));
        },
    },
    computed: {
        userRole() {
            return localStorage.getItem('userRole') || '';
        },
        isGeneralManager() {
            return this.userRole === 'GENERAL_MANAGER';
        },
        isAdmin() {
            return this.userRole === 'ADMIN';
        },
        isSuperAdmin() {
            return this.userRole === 'SUPER_ADMIN';
        }
    },
    methods: {
        toggleMobileMenu() {
            this.mobileMenuOpen = !this.mobileMenuOpen;
        },
        closeMobileMenu() {
            this.mobileMenuOpen = false;
        },
        toggleDesktopSidebar() {
            this.desktopSidebarOpen = !this.desktopSidebarOpen;
        },
        // Check if user can access a route based on path
        canAccessRoute(path) {
            // Super Admin has access to everything
            if (this.isSuperAdmin) {
                return true;
            }

            // Map paths to pageCodes
            const pathToPageCode = {
                '/dashboard': 'dashboard',
                '/support-tickets': 'support_tickets',
                '/view/teachers': 'teachers',
                '/teachers': 'teachers', // Alternative path
                '/add/teacher': 'teachers', // Create action
                '/teacher-calendar': 'teachers',
                '/teacher-schedule-management': 'teachers',
                '/students': 'students',
                '/add/student': 'students', // Create student
                '/new/student': 'new_students',
                '/allStudents': 'students',
                '/student-calendar': 'students',
                '/sessions': 'sessions',
                '/studentSessions': 'sessions',
                '/student-sessions-management': 'sessions',
                '/admin-student-booking': 'sessions',
                '/public-circles-management': 'public_circles_management',
                '/public-circles-management/attendance-logs': 'public_circles_management',
                '/public-circles-management/daily-chat-logs': 'public_circles_management',
                '/iqra/schedules': 'iqra_schedules',
                '/iqra/teacher-weekly-schedule': 'iqra_schedules',
                '/iqra/student-weekly-schedule': 'iqra_schedules',
                '/iqra/student-slot-enrollments': 'iqra_schedules',
                '/iqra/prayer-times-sync-status': 'iqra_schedules',
                '/live-sessions': 'iqra_schedules',
                '/iqra/subscription-packages': 'iqra_subscription_packages',
                '/iqra/student-subscriptions': 'iqra_student_subscriptions',
                '/mudarasa-sessions': 'mudarasa_sessions',
                '/sendMes': 'reports',
                '/reports': 'reports', // Alternative path
                '/session-report': 'reports',
                '/SummarySessionReport': 'reports',
                '/DailyStudentReport': 'reports',
                '/iqra/teacher-response-report': 'iqra_schedules',
                '/peer-review-report': 'peer_review_report',
                // Note: '/super-admin-general-report', '/tenants-management', '/general-managers-management' 
                // are Super Admin only and don't need permission checks (protected by isSuperAdmin)
                '/appSetting': 'app_settings',
                '/allAdmins': 'admins',
                '/addAdmin': 'admins',
                '/organizations': 'organizations',
                '/admin-messages': 'admin_messages',
                '/admin-notifications': 'admin_notifications',
                '/audit-logs': 'audit_logs',
                '/qualifications-management': 'qualifications_management',
                '/durations-management': 'durations_management',
                '/massarats-management': 'massarats_management',
                '/regions-management': 'regions_management',
                '/athan-times-management': 'athan_times_management',
                '/goals-management': 'goals_management',
                '/about-us-management': 'about_us_management',
                '/contact-us-management': 'contact_us_management',
                '/terms-and-conditions-management': 'terms_and_conditions_management',
                '/settings': 'settings',
                '/permission-groups': 'permission_groups',
                '/user-permissions': 'user_permissions',
                '/ai-tool-permissions': 'mcp_tool_permissions',
                '/hafed': 'hafed',
            };

            const pageCode = pathToPageCode[path];
            
            // If no pageCode mapping exists, allow access (backward compatibility)
            if (!pageCode) {
                return true;
            }

            // Check read permission for the page
            return permissionsComposable.hasPermission(pageCode, 'read');
        },
        logout() {
            axios.post(process.env.VUE_APP_SERVICE_URL + url.logout, null,
                {
                    headers: {
                        'X-Authorization': localStorage.getItem('token'),
                    }
                })
                .then(response => {
                    console.log(response);
                    localStorage.clear();
                    this.$router.push({ name: 'login' });
                })
                .catch(error => {
                    console.error('Error fetching data', error);
                    this.$router.push({ name: 'login' });
                });
        },
    },
}
</script>